import React, { useState, useRef, useEffect } from 'react';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';

/**
 * A tooltip that only shows if its content is truncated (overflows its container).
 * @param {Object} props
 * @param {string} props.content - The full text to show in the tooltip.
 * @param {React.ReactNode} props.children - The element containing the truncated text.
 * @param {string} [props.className] - Optional class for the trigger wrapper.
 */
export const TruncatedTooltip = ({ content, children, className }) => {
    const [isTruncated, setIsTruncated] = useState(false);
    const triggerRef = useRef(null);

    const checkTruncation = () => {
        const element = triggerRef.current;
        if (element) {
            // We check the first child because that's usually where the text and 
            // the truncation logic (like 'truncate' or 'line-clamp') resides.
            const target = element.firstElementChild || element;

            const isHorizontalOverflow = target.scrollWidth > target.clientWidth;
            const isVerticalOverflow = target.scrollHeight > target.clientHeight;

            setIsTruncated(isHorizontalOverflow || isVerticalOverflow);
        }
    };

    useEffect(() => {
        checkTruncation();
        // Add resize listener to re-check on window resize
        window.addEventListener('resize', checkTruncation);
        return () => window.removeEventListener('resize', checkTruncation);
    }, [content]);

    return (
        <TooltipProvider>
            <Tooltip open={isTruncated ? undefined : false}>
                <TooltipTrigger asChild>
                    <div
                        ref={triggerRef}
                        className={className}
                        onMouseEnter={checkTruncation}
                    >
                        {children}
                    </div>
                </TooltipTrigger>
                <TooltipContent>
                    <p className="max-w-xs break-words">{content}</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};
